import React from 'react';
import styled from 'styled-components';
import { useEditMode } from '../../shared/hooks/useEditMode';
import { SortDirection, WorkType, WorkSortOrder } from '../../shared/enum/EnumRepository';
import { useWorkPropertyVisible } from '../../shared/hooks/useApi/useWorkPropertyVisible';
import { useWorkListStore } from '../../shared/store/WorkListStore';
import { useUnsavedChanges } from '../../shared/hooks/useUnsavedChanges';

interface BlockWithSortVisibleProps {
  width: string;
  isColumn?: boolean;
  align?: 'left' | 'center';
  paddingLeft?: string;
  field: WorkSortOrder;
  value: string | WorkType;
}

const BlockWithSortVisible: React.FC<BlockWithSortVisibleProps> = ({
  width,
  isColumn,
  align = 'center',
  paddingLeft,
  field,
  value,
}) => {
  const { isEditMode } = useEditMode();
  const { workPropertyVisible } = useWorkPropertyVisible();
  const sortData = useWorkListStore((s) => s.sortData);
  const setSortData = useWorkListStore((s) => s.setSortData);
  const { requestExitEditMode } = useUnsavedChanges();

  if (!workPropertyVisible) return null;

  const handleDirectionChange = (field: WorkSortOrder) => {
    if (isEditMode) {
      requestExitEditMode({ confirmWhenClean: true });
      return;
    }

    // 현재 정렬 필드와 같으면 방향만 변경, 다르면 해당 필드로 정렬하고 ASC로 설정
    if (sortData.sort === field) {
      setSortData({
        ...sortData,
        direction: sortData.direction === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC
      });
    } else {
      setSortData({ sort: field, direction: SortDirection.ASC });
    }
  };

  return (
    <Block
      $width={width}
      $isColumn={isColumn}
      $align={align}
      onClick={() => handleDirectionChange(field)}
    >
      <ValueContainer $align={align}>
        <Value
          $isColumn={isColumn}
          $paddingLeft={paddingLeft}
        >
          {value}
        </Value>
        <SelectDirection
          $isASC={sortData.sort === field && sortData.direction === SortDirection.ASC}
          $isSortField={sortData.sort === field}
        >
          ▼
        </SelectDirection>
      </ValueContainer>
    </Block>
  )
}


const Block = styled.div<{
  $width: string;
  $isColumn: boolean | undefined;
  $align: 'left' | 'center';
}>`
  flex: ${({ $width }) => $width};

  display: flex;
  justify-content: ${({ $align }) => $align === 'left' ? 'flex-start' : 'center'};
  align-items: center;

  min-width: 0;

  cursor: pointer;
  user-select: none;

  border-bottom: ${({ $isColumn, theme }) =>
    $isColumn ? 'none' : `0.5px solid ${theme.colors.color_Gray_04}`};
`

const ValueContainer = styled.div<{ $align: 'left' | 'center' }>`
  width: 80%;
  display: flex;
  justify-content: ${({ $align }) => $align === 'left' ? 'flex-start' : 'center'};
  align-items: center;
`

const Value = styled.span<{
  $isColumn: boolean | undefined;
  $paddingLeft: string | undefined;
}>`
  white-space: nowrap;
  overflow-x: hidden;
  text-overflow: ellipsis;

  padding-left: ${({ $paddingLeft, $isColumn }) =>
    $paddingLeft ?? ($isColumn ? '10px' : '3px')};

  text-align: center;

  user-select: none;
  cursor: pointer;
`

const SelectDirection = styled.span<{ $isSortField: boolean, $isASC: boolean }>`
  margin-left: 4px;
  ${({ theme }) => theme.typography.Footer_02};
  color: ${({ theme, $isSortField }) => $isSortField ? theme.colors.color_Gray_03 : theme.colors.color_Gray_05};
  transform: ${({ $isASC, $isSortField }) => {
    if (!$isSortField) return "scaleY(1) scale(0.7)"; // 기본: dim 처리된 ▼
    return $isASC ? "scaleY(-1) scale(0.7)" : "scaleY(1) scale(0.7)"; // ASC면 뒤집기, DESC면 그대로
  }} !important;
  transition: transform 0.6s ease-in-out;
  display: inline-block;
`;

export default BlockWithSortVisible;
